<template>
  <section v-if="suggestedEventUris.length" class="py-10 xl:py-12">
    <div class="container mx-auto v-page-section-container-padding">
      <AtomHeadingPageSection class="mb-5 sm:mb-6 xl:mb-8" :title="$t('event-detail-neta-title')" />
      <ul class="grid grid-cols-12 gap-y-4 sm:gap-5 lg:gap-8">
        <li
          v-for="(suggestedEventUri, index) of sortedSuggestedEventUris"
          :key="suggestedEventUri"
          class="col-span-full sm:col-span-6 lg:col-span-4 2xl:col-span-3"
          :class="{ 'hidden sm:flex lg:hidden 2xl:flex': index >= 3, flex: index < 3 }"
        >
          <OrganismLoaderFromUri :uri="suggestedEventUri" @loaded="onEventLoaded">
            <template #loading>
              <MoleculeCardEventAndSeries :loading="true" />
            </template>
            <template #default="event">
              <MoleculeCardEventCompact
                v-viewport.25%.no-out.once="() => onViewport({ index, event })"
                :url="event.url"
                :when="new Date(event.eventDate)"
                :title="get(event, 'production.title') || event.title"
                :image="get(event, 'production.teaserImage')"
                :works="get(event, 'production.works')"
                @click="onEventCardClick"
              />
            </template>
          </OrganismLoaderFromUri>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import get from 'lodash/get'
import uniq from 'lodash/uniq'

const props = defineProps({
  production: { type: Object as PropType<Record<string, any>>, required: false, default: undefined },
  relatedEventUris: { type: Array as PropType<any[]>, required: false, default: () => [] },
})

const { locale } = useI18n()
const bridgeFetch = useBridgeFetch()

const viewItemList = setupGtmViewItemList('NETA')
const onViewport = ({ index, event }: { index: number; event: CmsData }) => viewItemList([event], index)
const suggestedEventUris = ref([...props.relatedEventUris])
const eventDateByUri = ref<Record<string, string>>({})
const gtmCdpAction = setupGtmCdpAction()

const onEventLoaded = ({ uri, page }: { uri: string; page?: CmsData }) => {
  if (page && page.eventDate) {
    eventDateByUri.value = { ...eventDateByUri.value, [uri]: page.eventDate }
  }
}

const sortedSuggestedEventUris = computed(() => {
  return [...suggestedEventUris.value].sort((a, b) => {
    const dateA = eventDateByUri.value[a]
    const dateB = eventDateByUri.value[b]
    if (!dateA && !dateB) return 0
    if (!dateA) return 1
    if (!dateB) return -1
    return new Date(dateA).getTime() - new Date(dateB).getTime()
  })
})

if (suggestedEventUris.value.length < 3 && props.production) {
  const productionUid = props.production.uid
  const { data } = await useAsyncData(`neta;${productionUid}`, () => bridgeFetch(`/api/neta/${locale.value}Default/${productionUid}`))
  if (data.value) {
    suggestedEventUris.value = uniq([...props.relatedEventUris, ...data.value.result.map((item: CmsData) => item.uri)].slice(0, 4))
  }
}

function onEventCardClick() {
  gtmCdpAction('CV07 - You might also like concert clicked')
}
</script>
